/**
 * Firebase Auth Error Message Utility
 *
 * Maps Firebase Auth error codes to user-friendly messages.
 * CRITICAL: Never expose raw error codes/messages to users.
 */

/**
 * Convert Firebase Auth error code to user-friendly message
 *
 * @param errorCode - Firebase Auth error code (e.g., 'auth/user-not-found')
 * @returns User-friendly error message
 */
export function getAuthErrorMessage(errorCode: string): string {
  const errorMessages: Record<string, string> = {
    // Email/Password errors
    'auth/user-not-found': 'No account found with this email address.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/email-already-in-use': 'An account with this email already exists.',
    'auth/weak-password': 'Password must be at least 6 characters long.',
    'auth/invalid-credential': 'Invalid email or password. Please try again.',

    // Account status errors
    'auth/user-disabled': 'This account has been disabled. Please contact support.',
    'auth/too-many-requests': 'Too many failed attempts. Please try again later.',

    // Google Sign-In errors
    'auth/popup-closed-by-user': 'Sign-in was cancelled. Please try again.',
    'auth/popup-blocked': 'Pop-up was blocked. Please allow pop-ups and try again.',
    'auth/account-exists-with-different-credential': 'An account already exists with the same email but different sign-in method.',

    // Password reset errors
    'auth/expired-action-code': 'This password reset link has expired. Please request a new one.',
    'auth/invalid-action-code': 'This password reset link is invalid. Please request a new one.',

    // Network errors
    'auth/network-request-failed': 'Network error. Please check your connection and try again.',

    // Operation errors
    'auth/operation-not-allowed': 'This sign-in method is not enabled. Please contact support.',
    'auth/requires-recent-login': 'Please sign out and sign in again to complete this action.',
  };

  // Return specific message or generic fallback
  return errorMessages[errorCode] || 'Something went wrong. Please try again.';
}

/**
 * Check if error is a Firebase Auth error
 *
 * @param error - Error object
 * @returns True if error has Firebase Auth error code
 */
export function isAuthError(error: unknown): error is { code: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    typeof (error as { code: unknown }).code === 'string'
  );
}
